import { Component, effect, inject, OnInit } from '@angular/core';
import { SidebarConfiguration } from '../../../models/sidebar-configuration';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { StoryStore } from '../../../stores/story/story.state';
import { TopicStore } from '../../../stores/topic/topic.state';
import { StateFilter } from '../../../stores/crud.state';
import { Story } from '../../../models/story';
import { IndicatorData } from '../../../models/indicator-data';
import { Bookmark } from '../../../models/bookmark';
import { Topic } from '../../../models/topic';
import { PickListModule } from 'primeng/picklist';
import {
  ConfirmationService,
  MessageService,
  PrimeTemplate,
} from 'primeng/api';
import { DataLayer } from '../../../models/data-layer';
import { SidebarService } from '../../../services/menu/sidebar.service';
import { getState } from '@ngrx/signals';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { Button } from 'primeng/button';
import { DividerModule } from 'primeng/divider';

interface PickListItem {
  name: string;
  type: string;
  id: string;
  icon: string;
  key: string;
  entity: IndicatorData | Bookmark | Topic | Story | DataLayer;
}

@Component({
  selector: 'app-publish-manager',
  standalone: true,
  imports: [PickListModule, PrimeTemplate, Button, DividerModule],
  templateUrl: './publish-manager.component.html',
  styleUrl: './publish-manager.component.scss',
})
export class PublishManagerComponent implements OnInit {
  readonly dataStore = inject(IndicatorDataStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly layerStore = inject(DataLayerStore);
  readonly typeStore = inject(DataLayerTypeStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly storyStore = inject(StoryStore);
  readonly topicStore = inject(TopicStore);

  initialized = false;
  selection: PickListItem[] = [];
  sourceItems: PickListItem[] = [];

  sidebarConfig: SidebarConfiguration = {
    cssClasses: 'w-11',
    icon: 'fas fa-globe',
    headerText: 'Veröffentlichungs-Manager',
  };

  constructor(
    private sidebarService: SidebarService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {
    effect(() => {
      const layerState = getState(this.layerStore);
      const dataState = getState(this.dataStore);
      const bookmarkState = getState(this.bookmarkStore);
      const storyState = getState(this.storyStore);
      const topicState = getState(this.topicStore);

      if (
        !this.initialized &&
        layerState.initialized &&
        dataState.initialized &&
        bookmarkState.initialized &&
        storyState.initialized &&
        topicState.initialized
      ) {
        setTimeout(() => {
          this.init();
          this.initialized = true;
        });
      }
    });
  }

  stateFilter: Partial<StateFilter> = {
    published: false,
  };

  ngOnInit(): void {
    setTimeout(() => {
      this.sidebarService.setConfiguration(this.sidebarConfig);
    }, 10);
    this.init();
  }

  /**
   * set all entity-store-filter to published = false
   */
  setFilter(): void {
    this.dataStore.updateFilter(this.stateFilter);
    this.layerStore.updateFilter(this.stateFilter);
    this.bookmarkStore.updateFilter(this.stateFilter);
    this.storyStore.updateFilter(this.stateFilter);
    this.topicStore.updateFilter(this.stateFilter);
  }

  /**
   * init unpublished data
   */
  init(): void {
    this.setFilter();
    const layers = this.layerStore.filteredItems();
    const data = this.dataStore.filteredItems();
    const bookmarks = this.bookmarkStore.filteredSorted();
    const topics = this.topicStore.filteredSorted();
    const stories = this.storyStore.filteredSorted();

    this.setSourceItems(data, bookmarks, topics, stories, layers);
  }

  /**
   * set source-items for pick-list
   *
   * @param data
   * @param bookmarks
   * @param topics
   * @param stories
   * @param layers
   */
  setSourceItems(
    data: IndicatorData[],
    bookmarks: Bookmark[],
    topics: Topic[],
    stories: Story[],
    layers: DataLayer[]
  ): void {
    this.sourceItems = [];

    data.forEach(item => {
      const indicator = this.indicatorStore.getById(item.indicator);
      const layer = this.layerStore.getById(item.dataLayer);
      if (!indicator || !layer) return;
      this.sourceItems.push({
        id: item._id,
        icon: 'fas fa-database',
        name: `${this.indicatorStore.getFullName(indicator)}: ${layer.year}`,
        type: 'IndicatorData',
        key: 'data',
        entity: item,
      });
    });

    layers.forEach(layer => {
      const type = this.typeStore.getById(layer.parent);
      if (!type) return;
      this.sourceItems.push({
        name: `${type.name}: ${String(layer.year)}`,
        icon: 'fas fa-calendar-days',
        id: layer._id,
        type: 'DataLayer',
        key: 'layers',
        entity: layer,
      });
    });

    bookmarks.forEach(bookmark => {
      this.sourceItems.push({
        name: bookmark.title,
        icon: 'fas fa-note-sticky',
        id: bookmark._id,
        type: 'Bookmark',
        key: 'bookmarks',
        entity: bookmark,
      });
    });

    stories.forEach(story => {
      this.sourceItems.push({
        name: story.title,
        icon: 'fas fa-file-lines',
        id: story._id,
        type: 'Story',
        key: 'stories',
        entity: story,
      });
    });

    topics.forEach(topic => {
      this.sourceItems.push({
        name: topic.title,
        icon: topic.icon,
        id: topic._id,
        type: 'Topic',
        key: 'topics',
        entity: topic,
      });
    });
  }

  /**
   * confirmation-dialog to publish selection
   *
   * @param event
   */
  confirm(event: any): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `${this.selection.length} Datensätze wirklich veröffentlichen?`,
      header: 'Auswahl veröffentlichen',
      icon: 'fas fa-question-circle',
      acceptLabel: 'ja, veröffentlichen',
      rejectLabel: 'nein, abbrechen',
      acceptIcon: 'fas fa-check-circle',
      rejectIcon: 'fas fa-times-circle',
      rejectButtonStyleClass: 'p-button-secondary p-button-text',
      accept: () => {
        this.publishSelection();
        this.messageService.add({
          severity: 'info',
          summary: 'Bestätigt',
          detail: 'Ihre Auswahl wurde erfolgreich veröffentlicht',
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'secondary',
          summary: 'Abgebrochen',
          detail: 'Ihre Auswahl wurde nicht veröffentlicht',
          life: 3000,
        });
      },
    });
  }

  /**
   * publish selection
   */
  publishSelection(): void {
    const idsToPublish: {
      [key: string]: (IndicatorData | DataLayer | Bookmark | Story | Topic)[];
    } = {
      layers: [],
      data: [],
      bookmarks: [],
      topics: [],
      stories: [],
    };

    this.selection.forEach(item => {
      const entity = item.entity;
      entity.published = true;
      idsToPublish[item.key]?.push(entity);
    });

    if (idsToPublish['layers'].length) {
      this.layerStore.updateMultiple({
        items: idsToPublish['layers'] as DataLayer[],
      });
    }

    if (idsToPublish['data'].length) {
      this.dataStore.updateMultiple({
        items: idsToPublish['data'] as IndicatorData[],
      });
    }

    if (idsToPublish['bookmarks'].length) {
      this.bookmarkStore.updateMultiple({
        items: idsToPublish['bookmarks'] as Bookmark[],
      });
    }

    if (idsToPublish['topics'].length) {
      this.topicStore.updateMultiple({
        items: idsToPublish['topics'] as Topic[],
      });
    }

    if (idsToPublish['stories'].length) {
      this.storyStore.updateMultiple({
        items: idsToPublish['stories'] as Story[],
      });
    }

    this.initialized = false;
    this.selection = [];
  }

  /**
   * empty selection
   */
  resetSelection(): void {
    this.selection = [];
    this.init();
  }
}
